import { useState, useEffect, useCallback, useRef } from 'react';
import { getToken } from '../utils/auth';

/**
 * Custom hook for Server-Sent Events (SSE) connection
 * Provides real-time notifications from the server
 */
export function useSSE() {
    const [isConnected, setIsConnected] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [lastNotification, setLastNotification] = useState(null);
    const eventSourceRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttempts = useRef(0);

    // Get the base URL from the same configured source as axios
    const getBaseUrl = useCallback(() => {
        return process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8080`;
    }, []);

    const handleEvent = useCallback((eventType, data) => {
        console.log('📡 SSE: Received event:', eventType, data);

        if (eventType === 'connected') {
            console.log('📡 SSE: Connection confirmed');
            return;
        }

        if (eventType === 'heartbeat') {
            // Heartbeat received, connection is alive
            return;
        }

        if (eventType === 'notification') {
            try {
                const notification = JSON.parse(data);
                const newNotification = {
                    ...notification,
                    receivedAt: new Date().toISOString(),
                    read: false,
                };

                setLastNotification(newNotification);
                setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep last 50

                console.log('📡 SSE: New notification:', notification.title);
            } catch (e) {
                console.error('📡 SSE: Failed to parse notification:', e);
            }
        }
    }, []);

    const scheduleReconnect = useCallback((connectFn) => {
        reconnectAttempts.current += 1;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);

        console.log(`📡 SSE: Reconnecting in ${delay / 1000}s (attempt ${reconnectAttempts.current})`);

        reconnectTimeoutRef.current = setTimeout(() => {
            connectFn();
        }, delay);
    }, []);

    const connect = useCallback(() => {
        const token = getToken();
        if (!token) {
            console.log('📡 SSE: No auth token, skipping connection');
            return;
        }

        // Close existing connection
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const baseUrl = getBaseUrl();
        const sseUrl = `${baseUrl}/api/v1/sse/events`;

        console.log('📡 SSE: Connecting to', sseUrl);

        // Use fetch with streaming for SSE with auth header
        const controller = new AbortController();

        fetch(sseUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'text/event-stream',
                'Cache-Control': 'no-cache',
            },
            signal: controller.signal,
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`SSE connection failed: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                setIsConnected(true);
                reconnectAttempts.current = 0;
                console.log('📡 SSE: Connected successfully');

                const processChunk = ({ done, value }) => {
                    if (done) {
                        console.log('📡 SSE: Stream ended');
                        setIsConnected(false);
                        scheduleReconnect(connect);
                        return;
                    }

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || ''; // Keep incomplete line in buffer

                    let currentEvent = '';
                    let currentData = '';

                    for (const line of lines) {
                        if (line.startsWith('event: ')) {
                            currentEvent = line.substring(7);
                        } else if (line.startsWith('data: ')) {
                            currentData = line.substring(6);
                        } else if (line === '' && currentEvent && currentData) {
                            // End of event
                            handleEvent(currentEvent, currentData);
                            currentEvent = '';
                            currentData = '';
                        }
                    }

                    reader.read().then(processChunk);
                };

                reader.read().then(processChunk);
            })
            .catch(error => {
                if (error.name !== 'AbortError') {
                    console.error('📡 SSE: Connection error:', error);
                    setIsConnected(false);
                    scheduleReconnect(connect);
                }
            });

        eventSourceRef.current = { close: () => controller.abort() };
    }, [getBaseUrl, handleEvent, scheduleReconnect]);

    const disconnect = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        setIsConnected(false);
    }, []);

    const clearNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
        if (lastNotification?.id === id) {
            setLastNotification(null);
        }
    }, [lastNotification]);

    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
        setLastNotification(null);
    }, []);

    // Connect on mount
    useEffect(() => {
        connect();
        return disconnect;
    }, [connect, disconnect]);

    // Reconnect when token changes
    useEffect(() => {
        const token = getToken();
        if (token && !isConnected) {
            connect();
        }
    }, [connect, isConnected]);

    return {
        isConnected,
        notifications,
        lastNotification,
        clearNotification,
        clearAllNotifications,
        reconnect: connect,
    };
}

export default useSSE;
